import auth from '@react-native-firebase/auth';
import { UserServiceClient } from 'fitbeat.api-clients';
import { UserProfile } from 'fitbeat.gym-core';
import { AgreementType, ICreatePreAccountUser, IDeeplinkParameters } from 'fitbeat.models';
import React, { PureComponent } from 'react';
import { translate } from 'react-i18next';
import { ActivityIndicator, Keyboard, KeyboardAvoidingView, Platform, StyleSheet, Text, View } from 'react-native';
import { connect } from 'react-redux';
import { Field, formValueSelector, reduxForm, SubmissionError } from 'redux-form';
import { IAppStore } from '../../dist/HomeWorkouts';
import { Button, ButtonSize, ButtonTypes } from '../components';
import CountryCodePicker from '../payments/components/CountryCodePicker';
import Theme from '../theme';
import { FormError, TextInput } from '../ui/components';
import {
  isEmailValid,
  isPhoneNumberValidNoCountryCode,
  isRequired, parseEmailForTextFieldFromURL,
} from '../utils';
import { formatMobileNumber } from '../utils';
import { Logger, parseMobileNumberForTextFieldFromURL } from '../utils';
import { createUnauthenticatedUserServiceClient, createUserServiceClient } from '../utils/apiClientFactory';
import { focusOnInput } from '../utils/FocusOnInput';
import { clearDeepLinkParams as clearDeepLinkParamsAction } from '../utils/notifications/actions';
import { defaultScale, scaleText } from '../utils/scaling';
import { isDesktop, isMobile } from '../utils/ScreenSizes';
import {
  preAccountCreateSubmit as preAccountCreateSubmitAction,
} from './actions';
import { TermsAndConditions } from './components';
import { buildSignupSource } from './components/create-platform-user';
import { styles as SharedStyles } from './SignupFormsStyles';

interface IProps {
  deepLinkParams: IDeeplinkParameters;
  error: string;
  navigation: {
    navigate: (route: string) => void;
  };
  t: (key: string) => string;
  handleSubmit: (fn: (values: ICreatePreAccountForm) => void) => () => any;
  initialize: (values: any) => void;
  mobileNumber: string;
  countryCode: string;
  valid: boolean;
  initialized: boolean;
  email: string;
  firstname: string;
  lastname: string;
  preAccountCreateSubmit: () => void;
  clearDeepLinkParams: (params: string[]) => void;
}

interface IState {
  loading: boolean;
  termsAccepted: boolean;
  autoSubmitError: null | Error;
}

interface ICreatePreAccountForm {
  email: string;
  firstname: string;
  lastname: string;
  countryCode: string;
  mobileNumber: string;
}

class CreatePreAccount extends PureComponent<IProps, IState> {
  private readonly unAuthenticatedUserServiceClient: UserServiceClient;
  private readonly authenticatedUserServiceClient: UserServiceClient;

  private lastnameInput: any;
  private mobilePhoneInput: any;
  private emailInput: any;

  constructor(props) {
    super(props);
    this.unAuthenticatedUserServiceClient = createUnauthenticatedUserServiceClient();
    this.authenticatedUserServiceClient = createUserServiceClient();
    this.state = {
      loading: true,
      termsAccepted: false,
      autoSubmitError: null,
    };
  }

  public createFirebaseUserForPreSignupAccount = async (continueSignupToken) => {
    const {
      email,
      password,
    } = await this.unAuthenticatedUserServiceClient.completeDemoStudioPreAccount(continueSignupToken);
    await auth().setPersistence(auth.Auth.Persistence.NONE);
    await auth().signInWithEmailAndPassword(email, password);
    return email;
  }

  public handleEmailAlreadyInUse = async (
    preSignupAccountWithEmail: { email: string, continueSignupToken?: string },
  ) => {
    const { t } = this.props;
    if (preSignupAccountWithEmail.continueSignupToken) {
      await this.createFirebaseUserForPreSignupAccount(preSignupAccountWithEmail.continueSignupToken);
      return this.props.navigation.navigate('HomeNavigator');
    }
    throw new Error(t('auth:existingAccountError'));
  }

  public submit = async (values: ICreatePreAccountForm) => {
    const { t } = this.props;
    const { termsAccepted } = this.state;
    const agreementSet = [
      AgreementType.Platform,
      AgreementType.Privacy,
      AgreementType.Membership,
      AgreementType.Home,
    ];

    this.setState({ autoSubmitError: null });

    if (!termsAccepted) {
      throw new SubmissionError({ _error: new Error(t('auth:pleaseAcceptTerms')) });
    }

    try {
      this.setState({ loading: true });
      this.props.preAccountCreateSubmit();
      await this.createUserAndLogin(values, agreementSet);
    } catch (error) {
      throw new SubmissionError({ _error: error });
    } finally {
      this.setState({ loading: false });
    }
  }

  public createUserAndLogin = async (values: ICreatePreAccountForm, agreementSet?) => {
    const { deepLinkParams: { gymName }, countryCode } = this.props;
    const formattedMobileNumber = countryCode
      ? formatMobileNumber(countryCode, values.mobileNumber)
      : values.mobileNumber;
    const preSignupAccountWithEmail = await this.unAuthenticatedUserServiceClient.getUserForPreAccountByEmail(
      values.email);
    if (preSignupAccountWithEmail) {
      await this.handleEmailAlreadyInUse(preSignupAccountWithEmail);
      return;
    }

    const signupSource = buildSignupSource(this.props.deepLinkParams);
    const createPreAccountUser: ICreatePreAccountUser = {
      email: values.email,
      mobileNumber: formattedMobileNumber,
      firstname: values.firstname,
      lastname: values.lastname,
      gymName,
      ...agreementSet ? { legalAgreements: agreementSet } : {},
      signupSource,
    };
    const user: UserProfile = await this.unAuthenticatedUserServiceClient.signupForPreAccount(createPreAccountUser);
    await this.createFirebaseUserForPreSignupAccount(user.continueSignupToken);
    this.props.clearDeepLinkParams(['email']);
    this.props.navigation.navigate('HomeNavigator');
  }

  public acceptTermsAndConditions = () => {
    this.setState({ termsAccepted: !this.state.termsAccepted });
    Keyboard.dismiss();
  }

  public componentDidMount = () => {
    const { email, firstname, lastname, mobileNumber, continueSignupToken } = this.props.deepLinkParams;
    const { initialize } = this.props;
    const parsedMobileNumber = parseMobileNumberForTextFieldFromURL(mobileNumber);
    const parsedEmail = parseEmailForTextFieldFromURL(email);
    const formValuesWithoutUndefined = JSON.parse(JSON.stringify({
      email: parsedEmail, firstname, lastname, mobileNumber: parsedMobileNumber,
    }));
    initialize(formValuesWithoutUndefined);
    if (continueSignupToken) {
      return this.userLinkedToAppWIthExistingPreAccount(continueSignupToken);
    }
    // wait for next render when redux form has initialized.
    setTimeout(this.autoSubmitFormIfValid);
  }

  public userLinkedToAppWIthExistingPreAccount = async (continueSignupToken) => {
    try {
      await this.createFirebaseUserForPreSignupAccount(continueSignupToken);
      this.props.navigation.navigate('HomeNavigator');
    } catch (e) {
      Logger.notify(e, (report) => {
        report.errorClass = 'CreatePreAccount';
        report.errorMessage = `${continueSignupToken} ${JSON.stringify(e)}`;
      });
    } finally {
      this.setState({ loading: false });
    }
  }

  public isFormFieldsValid(values: ICreatePreAccountForm) {
    return (
      !isEmailValid(values.email) &&
      !isPhoneNumberValidNoCountryCode(values.mobileNumber) &&
      !isRequired(values.firstname) &&
      !isRequired(values.lastname)
    );
  }

  public autoSubmitFormIfValid = async () => {
    const { countryCode, firstname, lastname, email, mobileNumber } = this.props;
    if (this.isFormFieldsValid({ email, mobileNumber, lastname, firstname, countryCode })) {
      try {
        await this.createUserAndLogin({ firstname, lastname, email, mobileNumber,
          countryCode });
      } catch (e) {
        this.setState({ autoSubmitError: e as Error });
      } finally {
        this.setState({ loading: false });
      }
    } else {
      this.setState({ loading: false });
    }
  }

  public showPhoneFieldLabel = () => {
    const { mobileNumber } = this.props;
    if (isDesktop()) {
      return true;
    }
    return !!mobileNumber;
  }

  public login = () => {
    const { navigation: { navigate } } = this.props;
    navigate('Login');
  }

  public render() {
    const shouldShowPhoneFieldLabel = this.showPhoneFieldLabel();
    const { t, handleSubmit, error } = this.props;
    const { loading, termsAccepted, autoSubmitError } = this.state;

    if (loading) {
      return (
        <View style={[styles.container, { justifyContent: 'center' }]}>
          <ActivityIndicator
            size={'small'}
            color={Theme.PRIMARY}
            animating
          />
        </View>
      );
    }

    return (
      <View style={[SharedStyles.container, styles.container]}>
        <View style={[styles.formContainer, isMobile() && { flex: 1 }]}>
          <Text style={styles.heading}>{t('auth:createPreAccountHeader')}</Text>
          <Field
            trim
            name='email'
            placeholder={t('email')}
            component={TextInput}
            returnKeyType='done'
            autoCapitalize='none'
            keyboardType='email-address'
            validate={isEmailValid}
            onSubmitEditing={() => focusOnInput(this.emailInput)}
          />
          <Field
            trim
            name='firstname'
            placeholder={t('firstname')}
            component={TextInput}
            returnKeyType='done'
            autoCapitalize='words'
            validate={isRequired}
            onSubmitEditing={() => focusOnInput(this.lastnameInput)}
          />
          <Field
            trim
            name='lastname'
            placeholder={t('lastname')}
            component={TextInput}
            returnKeyType='done'
            autoCapitalize='words'
            validate={isRequired}
            ref={(ref) => {
              this.lastnameInput = ref;
            }}
            onSubmitEditing={() => focusOnInput(this.lastnameInput)}
          />
          {shouldShowPhoneFieldLabel &&
            <Text style={styles.labelText}>{t('mobileNumber')}</Text>
          }
          <View style={styles.phoneNumberContainer}>
            <CountryCodePicker
              customControlStyle={{ backgroundColor: Theme.GREY_LIGHT }}
              customOptionStyle={{ backgroundColor: Theme.GREY_LIGHT, color: Theme.GREY_MEDIUM }}
              mainContainerStyle={shouldShowPhoneFieldLabel && { marginTop: 0 }}
              setCountryCode={() => null}
              setPhone={() => null}
            />
            <View style={{ flex: 1 }}>
              <Field
                trim
                name='mobileNumber'
                showLabel={false}
                returnKeyType='next'
                component={TextInput}
                placeholder={t('mobileNumber')}
                keyboardType='number-pad'
                mainContainerStyle={shouldShowPhoneFieldLabel && { marginTop: 0 }}
                validate={[isRequired, isPhoneNumberValidNoCountryCode]}
                ref={(ref) => {
                  this.mobilePhoneInput = ref;
                }}
                onSubmitEditing={() => Keyboard.dismiss()}
              />
            </View>
          </View>

          <TermsAndConditions
            canAccept={true}
            textStyle={styles.tandcText}
            preAgreementText={t('auth:byCreatingYouAgreeToTandC')}
            acceptTermsAndConditions={this.acceptTermsAndConditions}
            termsAccepted={termsAccepted}
            checkBoxSize={defaultScale(20)}
          />
          <FormError error={error} />
          <FormError error={autoSubmitError} />
        </View>
        <KeyboardAvoidingView
          keyboardVerticalOffset={120}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <Button title={t('auth:next')} onPress={handleSubmit(this.submit)} fontColor={Theme.WHITE}
                  isLoading={loading} size={ButtonSize.MEDIUM} />
          <Button
            containerStyle={styles.loginButton}
            title={t('auth:alreadyHaveAccount')}
            onPress={this.login}
            buttonType={ButtonTypes.TRANSPARENT}
          />
        </KeyboardAvoidingView>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  heading: {
    ...Theme.textStyle.heading,
    marginTop: defaultScale(30),
    marginBottom: defaultScale(isDesktop() ? 45 : 30),
  },
  container: {
    paddingBottom: defaultScale(15),
    height: '100%',
  },
  tandcText: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.GREY_MEDIUM,
  },
  formContainer: {
    marginBottom: defaultScale(15),
  },
  loginButton: {
    marginTop: defaultScale(10),
  },
  phoneNumberContainer: {
    flexDirection: 'row',
    width: '100%',
  },
  labelText: {
    ...Theme.textStyle.small,
    color: Theme.GREY,
    marginTop: defaultScale(5),
  },
});

const mapStateToProps = (state: IAppStore) => ({
  deepLinkParams: state.deepLinkParamaters,
  mobileNumber: formValueSelector('CreatePreAccount')(state, 'mobileNumber'),
  countryCode: formValueSelector('CountryCode')(state, 'countryCode'),
  email: formValueSelector('CreatePreAccount')(state, 'email'),
  firstname: formValueSelector('CreatePreAccount')(state, 'firstname'),
  lastname: formValueSelector('CreatePreAccount')(state, 'lastname'),
});

const mapDispatchToProps = (dispatch) => ({
  preAccountCreateSubmit: () => dispatch(preAccountCreateSubmitAction()),
  clearDeepLinkParams: (params: string[]) => dispatch(clearDeepLinkParamsAction(params)),
});

export default reduxForm({ form: 'CreatePreAccount' })
(translate('formFields', { wait: true })
(connect(mapStateToProps, mapDispatchToProps)(CreatePreAccount)));
